import {engineContext} from './context.js';

(function(global){
'use strict';

const VERSION='0.1.0-alpha';
const CAUSE_TYPES=[
  'RATES','INFLATION','LIQUIDITY','EARNINGS','COMMODITIES',
  'FX','GEOPOLITICS','POLICY','CREDIT','UNKNOWN'
];

function finite(v){return typeof v==='number'&&Number.isFinite(v)}

function normalizeCause(x){
  x=x||{};
  const type=CAUSE_TYPES.includes(x.type)?x.type:'UNKNOWN';
  const verification=['VERIFIED','PARTIAL','UNVERIFIED'].includes(x.verification)?x.verification:'UNVERIFIED';
  return Object.freeze({
    type,
    direction:['POSITIVE','NEGATIVE','MIXED','UNKNOWN'].includes(x.direction)?x.direction:'UNKNOWN',
    magnitude:finite(x.magnitude)?Math.max(0,Math.min(1,x.magnitude)):null,
    verification,
    independent:x.independent===true,
    source:x.source||null,
    note:x.note||null
  });
}

function normalizePacket(input){
  input=input||{};
  const causes=Array.isArray(input.causes)?input.causes.map(normalizeCause):[];
  return Object.freeze({
    transition:input.transition||null,
    causes:Object.freeze(causes),
    asOf:input.asOf||null
  });
}

function scoreCause(c){
  const base=finite(c.magnitude)?c.magnitude:0.25;
  const verify=c.verification==='VERIFIED'?1:c.verification==='PARTIAL'?0.6:0.25;
  const independence=c.independent?1:0.65;
  return base*verify*independence;
}

function rankCauses(input){
  const p=normalizePacket(input);
  return Object.freeze(
    p.causes
      .map(c=>Object.freeze({...c,score:scoreCause(c)}))
      .sort((a,b)=>b.score-a.score)
  );
}

function attribution(input){
  const p=normalizePacket(input);
  const ranked=rankCauses(p);
  const verifiedIndependent=ranked.filter(c=>c.verification==='VERIFIED'&&c.independent);
  const top=ranked[0]||null;

  let state='UNATTRIBUTED';
  if(verifiedIndependent.length>=2) state='MULTI_CAUSE_SUPPORTED';
  else if(verifiedIndependent.length===1) state='SINGLE_CAUSE_SUPPORTED';
  else if(ranked.length) state='TENTATIVE';

  return Object.freeze({
    state,
    topCause:top,
    ranked,
    verifiedIndependentCount:verifiedIndependent.length,
    tradeSignal:false,
    companyQualityOverride:false
  });
}

function contradiction(input){
  const a=attribution(input);
  if(!a.ranked.length) return Object.freeze({state:'NO_CAUSE_EVIDENCE',items:Object.freeze([])});
  const verified=a.ranked.filter(c=>c.verification==='VERIFIED');
  const pos=verified.some(c=>c.direction==='POSITIVE');
  const neg=verified.some(c=>c.direction==='NEGATIVE');
  if(pos&&neg){
    return Object.freeze({
      state:'CONFLICTING_CAUSES',
      items:Object.freeze(verified),
      rule:'Do not silently average conflicting macro evidence.'
    });
  }
  return Object.freeze({state:'NO_MAJOR_CONFLICT',items:Object.freeze([])});
}

function explain(input){
  const a=attribution(input);
  const c=contradiction(input);
  let headline='Cause of regime change is not established.';
  if(a.state==='MULTI_CAUSE_SUPPORTED'){
    headline=`Regime change has multiple supported drivers; strongest current driver: ${a.topCause.type}.`;
  }else if(a.state==='SINGLE_CAUSE_SUPPORTED'){
    headline=`One supported driver currently stands out: ${a.topCause.type}.`;
  }else if(a.state==='TENTATIVE'){
    headline=`Possible driver: ${a.topCause.type}, but verification is incomplete.`;
  }

  return Object.freeze({
    headline,
    attributionState:a.state,
    conflictState:c.state,
    topCause:a.topCause,
    ranked:a.ranked,
    tradeSignal:false
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const packet={causes:[
    {type:'RATES',direction:'NEGATIVE',magnitude:.9,verification:'VERIFIED',independent:true},
    {type:'CREDIT',direction:'NEGATIVE',magnitude:.7,verification:'VERIFIED',independent:true},
    {type:'GEOPOLITICS',direction:'NEGATIVE',magnitude:.8,verification:'UNVERIFIED',independent:false}
  ]};

  let a=attribution(packet);
  ok('multi-cause support detected',a.state==='MULTI_CAUSE_SUPPORTED');
  ok('rates ranks first',a.topCause.type==='RATES');
  ok('verified independent count correct',a.verifiedIndependentCount===2);
  ok('trade signal never emitted',a.tradeSignal===false);
  ok('company quality never overridden',a.companyQualityOverride===false);

  a=attribution({causes:[{type:'INFLATION',direction:'NEGATIVE',magnitude:.8,verification:'VERIFIED',independent:true}]});
  ok('single supported cause detected',a.state==='SINGLE_CAUSE_SUPPORTED');

  a=attribution({causes:[{type:'FX',direction:'NEGATIVE',magnitude:.8,verification:'PARTIAL',independent:true}]});
  ok('partial-only cause remains tentative',a.state==='TENTATIVE');

  a=attribution({causes:[]});
  ok('no evidence remains unattributed',a.state==='UNATTRIBUTED');

  let c=contradiction({causes:[
    {type:'RATES',direction:'NEGATIVE',magnitude:.8,verification:'VERIFIED',independent:true},
    {type:'LIQUIDITY',direction:'POSITIVE',magnitude:.7,verification:'VERIFIED',independent:true}
  ]});
  ok('conflicting verified causes surfaced',c.state==='CONFLICTING_CAUSES');

  c=contradiction(packet);
  ok('same-direction causes do not create conflict',c.state==='NO_MAJOR_CONFLICT');

  const ex=explain(packet);
  ok('explanation uses top supported cause',ex.headline.includes('RATES'));
  ok('explanation remains non-trading',ex.tradeSignal===false);

  const ranked=rankCauses({causes:[
    {type:'POLICY',magnitude:.9,verification:'UNVERIFIED',independent:false},
    {type:'EARNINGS',magnitude:.6,verification:'VERIFIED',independent:true}
  ]});
  ok('verification can outrank bigger unverified magnitude',ranked[0].type==='EARNINGS');

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,CAUSE_TYPES,normalizeCause,normalizePacket,scoreCause,rankCauses,attribution,contradiction,explain,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestGlobalRegimeCauseEngine=Object.freeze(API);

})(engineContext);
